/* ─────────────────────────────────────────────────────────────────────────
 * About page copy. Name, role and contact lines come from RESUME in the
 * cabinet's data.ts, so they only ever change in one place.
 * ───────────────────────────────────────────────────────────────────────── */

import type { ArcadeIconName, Tone } from '@/components/generals/arcade'

import { RESUME } from '@/components/landing/cabinet/data'

export type Stat = {
    label: string
    value: string
}

export type Rule = {
    icon: ArcadeIconName
    tone: Tone
    title: string
    body: string
}

export type Achievement = {
    value: string
    label: string
    note: string
}

const contactValue = (label: string): string => RESUME.contact.find((c) => c.label === label)?.value ?? ""

export const ABOUT = {
    meta: "Mara Ellison is a product designer in Lisbon. Read her story, the rules she designs by, and the numbers behind the games and apps she has shipped.",

    story: [
        "I'm Mara, a product designer in Lisbon. I design software the way a good game is designed: you pick it up, you get it straight away, and you want to come back tomorrow.",
        "I started out building interactive campaigns for museums and festivals, took a note-taking app from first sketch to launch at Fieldnote, and now lead design for a live puzzle game played by two million people a month at Kiln Games.",
        "I work close to the code. Most ideas become a clickable prototype within a day, and I stay with a feature until it's in people's hands, watching what changes and tuning from there."
    ],

    focus: ["Game UI", "Product design", "Prototyping", "Accessibility"],

    stats: [
        { label: "Class", value: RESUME.role },
        { label: "Level", value: "8 years shipping" },
        { label: "Base", value: contactValue("Location") },
        { label: "Status", value: contactValue("Status") }
    ] as Stat[],

    rulesIntro: "Three things I bring to every project, whether it's a game shop or a settings screen.",

    rules: [
        {
            icon: 'gamepad',
            tone: 'sage',
            title: "Teach by playing",
            body: "The best tutorials are the ones nobody notices. I design flows that show people what to do instead of telling them, so nobody needs a manual."
        },
        {
            icon: 'skills',
            tone: 'tan',
            title: "Prototype on day one",
            body: "An idea isn't real until you can click it. I put rough prototypes in front of people early and let what they do decide what ships."
        },
        {
            icon: 'heart',
            tone: 'peach',
            title: "Everyone gets to play",
            body: "Keyboard support, readable contrast and motion you can switch off are part of the first sketch, not a fix at the end."
        }
    ] as Rule[],

    achievementsIntro: "A few results from the work in the résumé, counted the way games count them.",

    achievements: [
        { value: "2M", label: "Players a month", note: "On the live puzzle game I design at Kiln Games." },
        { value: "+14%", label: "Day-seven retention", note: "After rebuilding onboarding and the in-game shop." },
        { value: "4 min", label: "To sell out", note: "A festival ticketing flow built at Northlight Studio." },
        { value: "300+", label: "Teams on Loop Kit", note: "My open-source kit of accessible components." },
        { value: "40K", label: "Surfers every morning", note: "Checking the Tidepool forecast before they paddle out." },
        { value: "48 h", label: "To an audience award", note: "Night Market, designed, drawn and coded for a game jam." }
    ] as Achievement[],

    cta: {
        kicker: "Continue?",
        title: "Ready for player two?",
        body: "Tell me about the game, app or idea you're working on. I read every message and reply within two working days.",
        primary: { href: '/contact', label: "Start a project" },
        secondary: { href: '/', label: "Back to the cabinet" }
    }
}
